import React from 'react';
import { Download, Edit, Calendar, CheckCircle } from 'lucide-react';

export type FormStatus = 'not_started' | 'draft' | 'in_progress' | 'completed';

export interface HeaderAction {
  id: string;
  label: string;
  icon?: React.ReactNode;
  variant?: 'primary' | 'secondary';
  onClick: () => void;
  disabled?: boolean;
}

interface ReviewHeaderProps {
  title: string;
  subtitle?: string;
  completionPercentage: number;
  status?: FormStatus; // Reserved for future use (e.g., badge display)
  lastUpdated?: string;
  onEdit?: () => void;
  onDownloadPDF?: () => void;
  actions?: HeaderAction[];
}

const ReviewHeader: React.FC<ReviewHeaderProps> = ({
  title,
  subtitle,
  completionPercentage,
  status: _status = 'draft', // Reserved for future badge display
  lastUpdated,
  onEdit,
  onDownloadPDF,
  actions = [],
}) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Unknown';
    }
  };

  const getProgressColor = () => {
    if (completionPercentage >= 100) return 'bg-green-600';
    if (completionPercentage >= 75) return 'bg-blue-600';
    if (completionPercentage >= 50) return 'bg-amber-500';
    return 'bg-gray-400';
  };

  const getStatusLabel = () => {
    if (completionPercentage >= 100) return 'Complete';
    if (completionPercentage >= 75) return 'Almost Complete';
    if (completionPercentage >= 50) return 'In Progress';
    if (completionPercentage > 0) return 'Started';
    return 'Not Started';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-kielis-navy">{title}</h2>
          {subtitle && <p className="text-sm text-gray-600 mt-1">{subtitle}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {onEdit && (
            <button
              onClick={onEdit}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              <Edit className="w-4 h-4" />
              <span>Edit</span>
            </button>
          )}
          {onDownloadPDF && (
            <button
              onClick={onDownloadPDF}
              className="flex items-center gap-2 px-4 py-2 bg-kielis-navy text-white rounded-md hover:bg-opacity-90 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Download PDF</span>
            </button>
          )}
          {actions.map((action) => (
            <button
              key={action.id}
              onClick={action.onClick}
              disabled={action.disabled}
              className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors disabled:opacity-50
                ${action.variant === 'primary'
                  ? 'bg-kielis-gold text-kielis-navy hover:bg-opacity-90'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
            >
              {action.icon}
              <span>{action.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            {completionPercentage >= 100 ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <div className={`w-4 h-4 rounded-full ${getProgressColor()}`} />
            )}
            <span className="font-medium text-gray-900">
              {getStatusLabel()}: {Math.round(completionPercentage)}%
            </span>
          </div>
          <div className="flex items-center gap-1 text-gray-600">
            <Calendar className="w-4 h-4" />
            <span>Last updated: {formatDate(lastUpdated)}</span>
          </div>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${getProgressColor()}`}
            style={{ width: `${Math.min(completionPercentage, 100)}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default ReviewHeader;
